import axios from 'axios';

// Base URL for the error logging API
// Note: We need /api/api/error-logs because nginx strips the first /api
const API_URL = process.env.REACT_APP_ERROR_LOGGING_URL || 'https://crm.maine-stream.com/api';
const ERROR_LOG_ENDPOINT = `${API_URL}/api/error-logs`;

// Log the configured endpoint for debugging
console.debug(`Error logger configured with endpoint: ${ERROR_LOG_ENDPOINT}`);

// Environment control - only log in production by default unless explicitly enabled
const LOGGING_ENABLED = 
  process.env.REACT_APP_LOGGING_ENABLED === 'true' || 
  process.env.NODE_ENV === 'production' || 
  process.env.NODE_ENV === 'test';

// Flag to prevent infinite loop when error logging fails
let isLoggingError = false;

// Log levels
export enum LogLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Error categories
export enum ErrorCategory {
  SERVER = 'server',
  CLIENT = 'client',
  NETWORK = 'network',
  AUTH = 'auth',
  DATABASE = 'database'
}

interface ErrorLogOptions {
  stack?: string;
  metadata?: Record<string, any>;
  source?: string;
  userId?: string | null;
  statusCode?: number;
  category?: ErrorCategory;
  path?: string;
  method?: string;
  requestPayload?: any;
  forceLog?: boolean;
}

interface ErrorLogResponse {
  id: string;
  timestamp: string;
  status: string;
}

// Determine error category based on error details
const determineErrorCategory = (error: any, statusCode?: number): ErrorCategory => {
  // Network errors
  if (
    error?.message?.includes('Network Error') || 
    error?.message?.includes('timeout') || 
    error?.code === 'ECONNABORTED'
  ) {
    return ErrorCategory.NETWORK;
  }
  
  // Auth errors
  if (statusCode === 401 || statusCode === 403) {
    return ErrorCategory.AUTH;
  }
  
  // Default to client for frontend errors
  return ErrorCategory.CLIENT;
};

// Main error logging function
export const logError = async (
  application: string,
  level: LogLevel,
  message: string,
  options: ErrorLogOptions = {}
): Promise<ErrorLogResponse | null> => {
  // Prevent infinite loop - if we're already logging an error, don't try to log the failure
  if (isLoggingError) {
    console.warn('Error logger: Prevented recursive logging attempt');
    return null;
  }
  
  if (!LOGGING_ENABLED && !options.forceLog) {
    console.debug('Error logging disabled in this environment');
    return null;
  }
  
  // Set flag to prevent recursive calls
  isLoggingError = true;
  
  try {
    const {
      stack,
      metadata = {},
      source = '',
      userId = null,
      statusCode,
      category,
      path = window.location.pathname,
      method,
      requestPayload
    } = options;
    
    const errorCategory = category || determineErrorCategory(metadata.originalError, statusCode);
    const userAgent = navigator.userAgent;
    
    const response = await axios.post(ERROR_LOG_ENDPOINT, {
      application,
      level,
      message,
      stack,
      metadata,
      source,
      userId,
      statusCode,
      category: errorCategory,
      path,
      method,
      requestPayload,
      userAgent
    });
    
    return response.data;
  } catch (error) {
    // Silently fail to prevent circular error logging
    // Only log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to log error:', error);
    }
    return null;
  } finally {
    // Always reset the flag after attempt (success or failure)
    isLoggingError = false;
  }
};

// Create application-specific logger
export const createErrorLogger = (application: string, defaultOptions: ErrorLogOptions = {}) => {
  if (!application) throw new Error('Application name is required');
  
  return {
    info: (message: string, options: ErrorLogOptions = {}) => 
      logError(application, LogLevel.INFO, message, { ...defaultOptions, ...options }),
    
    warning: (message: string, options: ErrorLogOptions = {}) => 
      logError(application, LogLevel.WARNING, message, { ...defaultOptions, ...options }),
    
    error: (messageOrError: string | Error, options: ErrorLogOptions = {}) => {
      if (messageOrError instanceof Error) {
        return logError(application, LogLevel.ERROR, messageOrError.message, {
          stack: messageOrError.stack,
          metadata: { originalError: messageOrError, ...options.metadata },
          ...defaultOptions,
          ...options
        });
      }
      return logError(application, LogLevel.ERROR, messageOrError, { ...defaultOptions, ...options });
    },
    
    critical: (messageOrError: string | Error, options: ErrorLogOptions = {}) => {
      if (messageOrError instanceof Error) {
        return logError(application, LogLevel.CRITICAL, messageOrError.message, {
          stack: messageOrError.stack,
          metadata: { originalError: messageOrError, ...options.metadata },
          ...defaultOptions,
          ...options
        });
      }
      return logError(application, LogLevel.CRITICAL, messageOrError, { ...defaultOptions, ...options });
    }
  };
};

// Default logger for this application
export const appLogger = createErrorLogger('utilities-crm', { source: 'frontend' });

const errorLogger = { logError, createErrorLogger, appLogger };
export default errorLogger;
